import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Lead, LoanStatus, LOAN_STATUS_CONFIG } from "@/types";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useModal } from "@/contexts/ModalContext";
import { Textarea } from "@/components/ui/textarea";

interface MoveStageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  onSave?: (
    newStatus: LoanStatus,
    removalReason?: string
  ) => void;
  onFollowUpRequired?: () => void;
}

// Stages before login (cannot go back here once logged in)
const preLoginStages: LoanStatus[] = [
  'new_lead',
  'in_progress',
  'ip_sanction',
];

// Stages before disbursement (post-login and pre-disbursement)
const stagesBeforeDisbursement: LoanStatus[] = [
  'new_lead',
  'in_progress',
  'ip_sanction',
  'query',
  'sanctioned',
  'verification_pending',
  'disbursal_pending',
];

// Stages after disbursement (excluding disbursement stages themselves)
const stagesAfterDisbursement: LoanStatus[] = [
  'otc',
  'payout_released',
  'closed',
];

// Special stages always available
const specialStages: LoanStatus[] = [
  'removed',
  'followup_later',
];

// Stages controlled by disbursement form only
const disbursementControlledStages: LoanStatus[] = [
  'partial_disbursed',
  'disbursed',
];

const MAX_REMOVAL_REASON_LENGTH = 300;

export function MoveStageDialog({
  open,
  onOpenChange,
  lead,
  onSave,
  onFollowUpRequired,
}: MoveStageDialogProps) {
  const [newStatus, setNewStatus] = useState<LoanStatus>(lead.status);
  const [removalReason, setRemovalReason] = useState("");
  const { registerModal, unregisterModal } = useModal();

  useEffect(() => {
    if (!open) return;
    registerModal("lead-move-stage-dialog", () => onOpenChange(false), 1);
    return () => unregisterModal("lead-move-stage-dialog");
  }, [open, onOpenChange, registerModal, unregisterModal]);

  // Determine which stages are available based on disbursement and login status
  const availableStatuses = useMemo(() => {
    const hasDisbursements =
      lead.disbursements && lead.disbursements.length > 0;
    const hasLoginRecords = lead.loginRecords && lead.loginRecords.length > 0;

    if (hasDisbursements) {
      // Rule 5: Once disbursement done, show only stages after disbursement + follow up later
      return [...stagesAfterDisbursement, "followup_later"] as LoanStatus[];
    } else if (hasLoginRecords) {
      // Once login filed, cannot go back to pre-login stages
      const postLoginStages = stagesBeforeDisbursement.filter(
        (s) => !preLoginStages.includes(s),
      );
      return [...postLoginStages, ...specialStages] as LoanStatus[];
    } else {
      // Before disbursement, show all stages before disbursement + removed + follow up later
      return [...stagesBeforeDisbursement, ...specialStages] as LoanStatus[];
    }
  }, [lead.disbursements, lead.loginRecords]);

  // Reset dialog state on open and ensure selected status is always valid.
  useEffect(() => {
    if (!open) return;

    setRemovalReason("");
    if (availableStatuses.includes(lead.status)) {
      setNewStatus(lead.status);
      return;
    }
    if (availableStatuses.length > 0) {
      setNewStatus(availableStatuses[0] as LoanStatus);
    }
  }, [open, lead.status, availableStatuses]);

  const handleSubmit = () => {
    if (newStatus === "removed" && !removalReason.trim()) {
      toast.error("Please enter removal reason");
      return;
    }
    if (newStatus === "followup_later") {
      onOpenChange(false);
      onFollowUpRequired?.();
      return;
    }
    if (onSave) {
      onSave(newStatus, removalReason);
    }
    toast.success(`Lead moved to ${LOAN_STATUS_CONFIG[newStatus].label}`);
    onOpenChange(false);
  };

  const hasDisbursements = lead.disbursements && lead.disbursements.length > 0;
  const hasLoginRecords = lead.loginRecords && lead.loginRecords.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move Lead Stage</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {hasDisbursements && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-sm text-blue-900">
                This lead has disbursements. Only post-disbursement stages are
                available.
              </AlertDescription>
            </Alert>
          )}

          {!hasDisbursements && hasLoginRecords && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-900">
                This lead has a login filed. Pre-login stages are no longer
                available.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label>Current Stage</Label>
            <div className="p-3 rounded-xl bg-secondary text-sm font-medium">
              {LOAN_STATUS_CONFIG[lead.status].label}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="newStatus">Move to</Label>
            <Select
              value={newStatus}
              onValueChange={(v) => setNewStatus(v as LoanStatus)}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {LOAN_STATUS_CONFIG[status].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {newStatus === "removed" && (
              <div className="space-y-2">
                <Label>Reason for Removal</Label>
                <Textarea
                  placeholder="Enter reason..."
                  value={removalReason}
                  onChange={(e) => setRemovalReason(e.target.value)}
                  maxLength={MAX_REMOVAL_REASON_LENGTH}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {removalReason.length}/{MAX_REMOVAL_REASON_LENGTH}
                </p>
              </div>
            )}
          </div>

          {/* Info about disbursement-controlled stages */}
          {!hasDisbursements && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs text-muted-foreground">
                <strong>Note:</strong> "Partial Disbursed" and "Disbursed"
                stages can only be set through the Disbursement Form.
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={newStatus === lead.status}
            className="rounded-xl"
          >
            Move Stage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}